import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { StatusBadge } from '@/components/ui/status-badge';
import { api } from '@/lib/api';

interface WorkflowView {
  key: string;
  version: number;
  domain: string;
  name: string;
  description: string;
  inputSchema: string;
  tool: string | null;
  checksum: string;
  configOverridden: boolean;
  steps: { key: string; type: string; description: string; conditional: boolean }[];
}

/** Published workflow definitions (read-only: they are code, versioned and locked; ADR-031). */
export function WorkflowsPage() {
  const q = useQuery({
    queryKey: ['workflows'],
    queryFn: () => api<{ items: WorkflowView[] }>('/api/v1/workflows'),
  });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Workflows"
        description="Published workflow definitions and their versions. Definitions are code: a published version never changes (checksum); a change is a new version."
      />
      {q.isError && <ErrorNote error={q.error} />}
      <Card>
        <CardBody className="p-0">
          <Table>
            <thead>
              <tr>
                <Th>Workflow</Th>
                <Th>Version</Th>
                <Th>Domain</Th>
                <Th>Tool</Th>
                <Th>Steps</Th>
                <Th>Checksum</Th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.items ?? []).map((w) => {
                const id = `${w.key}@${w.version}`;
                return (
                  <tr
                    key={id}
                    className="cursor-pointer hover:bg-surface-muted"
                    onClick={() => setOpen(open === id ? null : id)}
                  >
                    <Td>
                      <span className="font-medium">{w.name}</span>
                      <span className="block text-xs text-muted">
                        <Mono>{w.key}</Mono>
                        {w.configOverridden && ' · configured for this environment'}
                      </span>
                    </Td>
                    <Td>v{w.version}</Td>
                    <Td>{w.domain}</Td>
                    <Td>{w.tool ? <Mono>{w.tool}</Mono> : '—'}</Td>
                    <Td>{w.steps.length}</Td>
                    <Td>
                      <Mono title={w.checksum}>{w.checksum.slice(0, 12)}…</Mono>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </CardBody>
      </Card>
      {(q.data?.items ?? [])
        .filter((w) => `${w.key}@${w.version}` === open)
        .map((w) => (
          <Card key={open}>
            <CardHeader>
              <CardTitle>
                {w.name} v{w.version}
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <p className="text-sm">{w.description}</p>
              <p className="text-xs text-muted">
                Input <Mono>{w.inputSchema}</Mono>
              </p>
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Step</Th>
                    <Th>Type</Th>
                    <Th>What it does</Th>
                  </tr>
                </thead>
                <tbody>
                  {w.steps.map((s, i) => (
                    <tr key={s.key}>
                      <Td>{i + 1}</Td>
                      <Td>
                        <Mono>{s.key}</Mono>
                        {s.conditional && (
                          <span className="block text-xs text-muted">conditional</span>
                        )}
                      </Td>
                      <Td>
                        <Mono>{s.type}</Mono>
                      </Td>
                      <Td>{s.description}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        ))}
    </div>
  );
}

interface PolicyView {
  policies: { id: string; effect: 'permit' | 'forbid'; text: string }[];
  roleClearance: Record<string, number>;
}

const CLEARANCE = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];

/** Active authorization policies (read-only: Cedar policies are code; ADR-031). */
export function PoliciesPage() {
  const q = useQuery({
    queryKey: ['policies'],
    queryFn: () => api<PolicyView>('/api/v1/policies'),
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Policies"
        description="Authorization policies in force (Cedar, default deny) and the data classification each role may see. Policies are code: they change through a reviewed release."
      />
      {q.isError && <ErrorNote error={q.error} />}
      {q.data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Classification clearance per role</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <Table>
                <thead>
                  <tr>
                    <Th>Role</Th>
                    <Th>Highest classification</Th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(q.data.roleClearance).map(([role, rank]) => (
                    <tr key={role}>
                      <Td>
                        <Mono>{role}</Mono>
                      </Td>
                      <Td>{CLEARANCE[rank] ?? rank}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Policies ({q.data.policies.length})</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              {q.data.policies.map((p) => (
                <details key={p.id} className="rounded-md border border-border p-2">
                  <summary className="flex cursor-pointer items-center gap-2 text-sm">
                    <StatusBadge status={p.effect === 'forbid' ? 'fail' : 'ok'} label={p.effect} />
                    <Mono>{p.id}</Mono>
                  </summary>
                  <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-xs">{p.text}</pre>
                </details>
              ))}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
